import type { View } from './views'

export type Turn = { role: 'user' | 'assistant'; content: string; view?: View }

/**
 * The two ways a reply changes while it is being written.
 *
 * Pure and in their own file because they got this wrong once in a way that
 * was almost impossible to see: `appendToLast` rebuilt the turn as
 * `{ role, content }`, which quietly dropped the `view` that had been attached
 * to it a moment earlier. Pearl offered a group, the button appeared, and the
 * very next word she wrote deleted it — so it flashed on screen for a frame
 * and vanished. Everything about the offer worked; the reducer threw it away.
 *
 * **Preserve the turn and change one field.** Never rebuild it.
 */

export function withChunk(turns: Turn[], chunk: string): Turn[] {
  const last = turns[turns.length - 1]
  if (!last || last.role !== 'assistant') return turns
  const copy = turns.slice()
  copy[copy.length - 1] = { ...last, content: last.content + chunk }
  return copy
}

export function withView(turns: Turn[], view: View): Turn[] {
  const last = turns[turns.length - 1]
  if (!last || last.role !== 'assistant') return turns
  const copy = turns.slice()
  copy[copy.length - 1] = { ...last, view }
  return copy
}

/**
 * Drop a placeholder nothing ever arrived in, so an error does not sit above an
 * empty bubble — but keep one that carries a view, because a reply can
 * legitimately be nothing but an offer.
 */
export function dropEmptyPlaceholder(turns: Turn[]): Turn[] {
  const last = turns[turns.length - 1]
  const empty = last && last.role === 'assistant' && last.content === '' && !last.view
  return empty ? turns.slice(0, -1) : turns
}

/**
 * Pearl's `**bold**`, split into parts a component can render.
 *
 * WHY IT RETURNS DATA RATHER THAN ELEMENTS
 *
 * It used to build the `<strong>` tags itself, inside `Pearl.tsx`, with a
 * comment saying it was exported "so the assertions can reach it". Nothing
 * reached it: importing that component into a test would drag in the router,
 * the roster and the session, none of which exist outside a browser. The
 * comment had been true of an intention rather than of the code.
 *
 * DELIBERATELY ONLY BOLD. A full markdown renderer would also have to decide
 * what to do with headings, links and code fences arriving a character at a
 * time, and half-received markup rendering wrongly for a second is worse than a
 * stray asterisk.
 *
 * ── THE HALF-ARRIVED MARKER, WHICH IS THE WHOLE PROBLEM ─────────────────────
 *
 * Pearl's answers stream in. "click **Add** in the tag row" passes through
 * `click **`, `click **A`, `click **Ad` on its way — an unmatched marker, on
 * screen, for every bold word she ever writes.
 *
 * The previous rule was "fewer than three segments, leave it alone", which put
 * literal asterisks on screen during exactly that window. On the archetypal
 * how-do-I answer — one bold word, no other markers — that is the live-site bug
 * this function was written for, arriving a second time through the front door.
 * Worse, a reply cut off mid-marker left `**Add` on screen permanently.
 *
 * So an unmatched marker is treated as an OPENING one — its text renders bold
 * immediately and stays bold when the closing marker lands, with no flicker.
 *
 * UNLESS the text after it starts with a space, or there is none. `**` followed
 * by a space is not how anybody opens a bold word, and it is how Pearl would
 * write a literal pair of asterisks. That case keeps its markers and stays
 * plain, so "press the ** button" does not turn the rest of the sentence bold.
 */
export function emphasisParts(text: string): { text: string; bold: boolean }[] {
  const parts = text.split('**')
  if (parts.length === 1) return [{ text, bold: false }]

  // An even number of segments means an odd number of markers: one is unmatched,
  // and it is always the last, because text arrives from the left.
  const unmatched = parts.length % 2 === 0
  const tail = parts[parts.length - 1]
  const before = parts[parts.length - 2] ?? ''
  /**
   * An unmatched marker opens a bold run only where it LOOKS like an opening
   * one: nothing or whitespace in front of it, and a non-space after.
   *
   * The whitespace-after half stops "press the ** button" bolding the rest of
   * the sentence. The whitespace-BEFORE half was added a review later, after
   * `2**10 is 1024` came back as "2" followed by the rest of the message in
   * bold — a marker in the middle of a word is arithmetic or a filename, never
   * emphasis, and treating it as emphasis swallows everything after it.
   */
  const opener = tail !== '' && !/^\s/.test(tail) && (before === '' || /\s$/.test(before))
  const literalTail = unmatched && !opener

  const out: { text: string; bold: boolean }[] = []
  const upTo = literalTail ? parts.length - 1 : parts.length
  for (let i = 0; i < upTo; i++) {
    if (parts[i] === '') continue
    out.push({ text: parts[i], bold: i % 2 === 1 })
  }
  if (literalTail) out.push({ text: '**' + tail, bold: false })
  return out.length ? out : [{ text: '', bold: false }]
}
